import React, { useState } from 'react';
import { motion } from 'framer-motion';

interface RegisterButtonInFormProps {
  onClick: () => void;
}

/**
 * Register Button Component
 * Animated button with ripple and gradient effects
 */
const RegisterButtonInForm: React.FC<RegisterButtonInFormProps> = ({ onClick }) => {
  const [isHovered, setIsHovered] = useState(false);

  return (
    <motion.button
      onClick={onClick}
      type="submit"
      className={`
        relative overflow-hidden text-white font-bold 
        py-2 px-6 text-sm md:py-3.5 md:px-10 md:text-base 
        rounded-full flex items-center justify-center transition-all 
        disabled:opacity-70 disabled:cursor-not-allowed 
        bg-gradient-to-r from-red-950 via-red-800 to-red-950 bg-[length:200%_100%]
        
        after:content-[''] after:absolute after:top-1/2 after:left-1/2 
        after:w-[1px] after:h-[1px] after:bg-red-600 after:rounded-full 
        after:-translate-x-1/2 after:-translate-y-1/2 after:scale-100 
        after:transition-[transform,width,height] after:duration-500 after:ease-in-out 
        after:z-0 after:pointer-events-none
        
        hover:after:w-[300px] hover:after:h-[300px] hover:after:-translate-x-1/2 
        hover:after:-translate-y-1/2 hover:after:scale-100
      `}
      initial={{
        boxShadow: '0px 0px 10px 0px rgba(190, 61, 42, 0.5)',
      }}
      animate={{
        backgroundPosition: ['0% center', '100% center', '0% center'],
        boxShadow: [
          '0px 0px 5px 0px rgba(190, 61, 42, 0.5)',
          '0px 0px 20px 8px rgba(190, 61, 42, 0.4), 0px 0px 40px 15px rgba(190, 61, 42, 0.2)',
          '0px 0px 5px 0px rgba(190, 61, 42, 0.5)',
        ],
      }}
      transition={{
        backgroundPosition: {
          duration: 8,
          repeat: Infinity,
          ease: 'linear',
        },
        boxShadow: {
          duration: 8,
          times: [0, 0.5, 1],
          repeat: Infinity,
          ease: 'easeInOut',
        },
      }}
      whileHover={{
        scale: 1.05,
        boxShadow:
          '0px 0px 25px 12px rgba(190, 61, 42, 0.6), 0px 0px 40px 20px rgba(190, 61, 42, 0.4)',
        transition: { duration: 0.3 },
      }}
      whileTap={{
        scale: 0.95,
        transition: { duration: 0.1 },
      }}
      onHoverStart={() => setIsHovered(true)}
      onHoverEnd={() => setIsHovered(false)}
    >
      <motion.div
        className="flex items-center relative z-10"
        initial={false}
        animate={
          isHovered
            ? {
                x: [0, 6],
                transition: { duration: 0.3 },
              }
            : {
                y: [0, -2, 0, 2, 0],
                rotate: [0, 2, 0, -2, 0],
                transition: {
                  duration: 5,
                  times: [0, 0.2, 0.5, 0.8, 1],
                  repeat: Infinity,
                  ease: 'easeInOut',
                },
              }
        }
      >
        <span className="mr-2 text-xs md:text-sm">ĐĂNG KÝ</span>

        <motion.svg
          className="h-5 w-5 md:h-6 md:w-6"
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 24 24"
          fill="currentColor"
          initial={{ x: -5, opacity: 0 }}
          animate={{ x: 0, opacity: 1 }}
          transition={{ duration: 0.3 }}
        >
          <path d="M12 4l-1.41 1.41L16.17 11H4v2h12.17l-5.58 5.59L12 20l8-8-8-8z" />
        </motion.svg>
      </motion.div>

      {/* Moving light effect */}
      <motion.div
        className="absolute top-0 left-0 w-20 h-full bg-white/10 skew-x-[20deg] z-10"
        animate={{
          left: ['-25%', '125%', '-25%'],
        }}
        transition={{
          duration: 3,
          repeat: Infinity,
          ease: 'easeInOut',
          repeatDelay: 2,
        }}
      />
    </motion.button>
  );
};

export default RegisterButtonInForm;
